import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { QrScanner } from '@/components/tickets/QrScanner';
import { checkinCopy, useCheckinTicket, type CheckinOutcome } from '@/data/ticketAdmission';

// Shared ticket check-in panel (manual entry + camera scan + result). Used by the
// admin ticket-admission desk and the faculty check-in route — door staff at a
// recital are often instructors, and the ticket-checkin Edge Function allows
// admin/faculty/service. Reissue is admin-only and lives only on the admin desk.
export function TicketCheckInPanel() {
  const [code, setCode] = useState('');
  const [showId, setShowId] = useState('');
  const checkin = useCheckinTicket();

  function submit(payload: string) {
    const value = payload.trim();
    if (!value || checkin.isPending) return;
    checkin.mutate({ qrPayload: value, showId: showId.trim() || null });
  }

  return (
    <section className="surface-elevated space-y-4 p-5">
      <h2 className="font-[var(--font-display)] text-[var(--text-xl)]">Check in</h2>

      <QrScanner
        disabled={checkin.isPending}
        onResult={(text) => {
          setCode(text);
          submit(text);
        }}
      />

      <form
        className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          submit(code);
        }}
      >
        <div className="space-y-1">
          <Label htmlFor="checkin-code">Ticket code</Label>
          <Input
            id="checkin-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Paste or type the ticket QR code"
            autoComplete="off"
          />
        </div>
        <Button type="submit" disabled={checkin.isPending || !code.trim()}>
          {checkin.isPending ? 'Checking…' : 'Check in'}
        </Button>
      </form>

      <details>
        <summary className="cursor-pointer text-[var(--color-ink-muted)] text-[var(--text-sm)]">
          Limit to a specific show (optional)
        </summary>
        <div className="mt-2 max-w-sm space-y-1">
          <Label htmlFor="checkin-show">Show ID</Label>
          <Input
            id="checkin-show"
            value={showId}
            onChange={(e) => setShowId(e.target.value)}
            placeholder="Only admit tickets for this show"
            autoComplete="off"
          />
        </div>
      </details>

      <div aria-live="polite">
        {checkin.isError && (
          <p role="alert" className="text-[var(--color-danger)] text-[var(--text-sm)]">
            {checkin.error instanceof Error ? checkin.error.message : 'Check-in failed.'}
          </p>
        )}
        {checkin.data && <CheckinResult result={checkin.data} />}
      </div>
    </section>
  );
}

function CheckinResult({ result }: { result: CheckinOutcome }) {
  const copy = checkinCopy(result.state);
  return (
    <div className="surface space-y-2 p-3" data-testid="checkin-result">
      <Badge tone={copy.tone}>{copy.label}</Badge>
      {result.ticket && (
        <div className="text-[var(--text-sm)]">
          <p className="font-medium">{result.ticket.seat_label ?? 'Seat unknown'}</p>
          <p className="text-[var(--color-ink-muted)]">
            {result.ticket.show_name ?? 'Show'} · {result.ticket.family_name ?? 'Guest'}
          </p>
        </div>
      )}
      {result.state === 'already_checked_in' && result.checked_in_at && (
        <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
          First checked in {new Date(result.checked_in_at).toLocaleString()}
        </p>
      )}
    </div>
  );
}
